import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Scissors, User, LogOut, Menu, X, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { API_BASE_URL } from '../../../config';
import AgendarCitaModal from '../AgendarCitaModal/AgendarCitaModal';
import DatosPersonales from './DatosPersonales';
import './view_client.css';

const APPOINTMENTS_PAGE_SIZE = 20;

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatTime = (timeStr) => {
  if (!timeStr) return '—';
  return timeStr.substring(0, 5);
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(value || 0);
};

const statusLabel = {
  pending: 'Pendiente',
  confirmed: 'Confirmada',
  completed: 'Completada',
  cancelled: 'Cancelada',
};

export default function ViewClient() {
  const [appointments, setAppointments] = useState([]);
  const [totalAppointments, setTotalAppointments] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [userName, setUserName] = useState('');
  const [clientData, setClientData] = useState(null);
  const [showAgendarModal, setShowAgendarModal] = useState(false);
  const [section, setSection] = useState('citas');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const navigate = useNavigate();

  const showToast = useCallback((type, message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 4500);
  }, []);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleNavCitas = () => {
    setSection('citas');
    closeMobileMenu();
  };

  const handleNavDatos = () => {
    setSection('datos');
    closeMobileMenu();
  };

  const applyProfile = useCallback((profile) => {
    const name = profile?.name || 'Cliente';
    setUserName(name);
    setClientData({
      nombre: profile?.name || '',
      email: profile?.email || '',
      telefono: profile?.phone || '',
    });
  }, []);

  const loadDashboard = useCallback(async (offset = 0, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_BASE_URL}/client/dashboard?limit=${APPOINTMENTS_PAGE_SIZE}&offset=${offset}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) {
        const data = await res.json();
        applyProfile(data.profile);
        const items = Array.isArray(data.appointments) ? data.appointments : [];
        setAppointments((prev) => (append ? [...prev, ...items] : items));
        setTotalAppointments(data.total ?? items.length);
        setHasMore(Boolean(data.has_more));
      } else {
        if (!append) {
          setAppointments([]);
          setTotalAppointments(0);
          setHasMore(false);
        }
        if (res.status !== 401) {
          showToast('error', 'No se pudo cargar tu perfil. Intenta de nuevo.');
        }
      }
    } catch {
      if (!append) {
        setAppointments([]);
        setTotalAppointments(0);
        setHasMore(false);
      }
      showToast('error', 'Error de conexión. Verifica tu internet.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [applyProfile, showToast]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('userRole');
    if (!token || role !== 'client') {
      navigate('/login');
      return;
    }
    loadDashboard(0, false);
  }, [navigate, loadDashboard]);

  const handleLoadMore = () => {
    if (!hasMore || loadingMore) return;
    loadDashboard(appointments.length, true);
  };

  const handleReservationSuccess = () => {
    setShowAgendarModal(false);
    showToast('success', '¡Cita agendada exitosamente! Se envió la confirmación a tu correo electrónico.');
    loadDashboard(0, false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    navigate('/login');
  };

  if (!localStorage.getItem('token') || localStorage.getItem('userRole') !== 'client') {
    return null;
  }

  return (
    <div className={`view-client-page ${mobileMenuOpen ? 'view-client-page-menu-open' : ''}`}>

      {toast && (
        <div className={`view-client-toast view-client-toast-${toast.type}`} role="alert">
          {toast.type === 'success'
            ? <CheckCircle size={20} />
            : <AlertCircle size={20} />
          }
          <span>{toast.message}</span>
          <button
            type="button"
            className="view-client-toast-close"
            onClick={() => setToast(null)}
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </div>
      )}

      <header className="view-client-header">
        <div className="view-client-header-left">
          <div className="view-client-brand">
            <Scissors size={24} />
            <span>GENUINO'S</span>
          </div>
          <nav className="view-client-nav view-client-nav-desktop">
            <button
              type="button"
              className={`view-client-nav-item ${section === 'citas' ? 'view-client-nav-item-active' : ''}`}
              onClick={() => setSection('citas')}
            >
              <Calendar size={18} />
              Mis citas
            </button>
            <button
              type="button"
              className={`view-client-nav-item ${section === 'datos' ? 'view-client-nav-item-active' : ''}`}
              onClick={() => setSection('datos')}
            >
              <User size={18} />
              Datos personales
            </button>
          </nav>
          <button
            type="button"
            className="view-client-hamburger"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Abrir menú"
          >
            <Menu size={24} />
          </button>
        </div>
        <div className="view-client-header-right view-client-header-right-desktop">
          <span className="view-client-user">Hola, {userName}</span>
          <button type="button" className="view-client-logout" onClick={handleLogout}>
            <LogOut size={18} />
            Salir
          </button>
        </div>
      </header>

      <div
        className={`view-client-mobile-overlay ${mobileMenuOpen ? 'view-client-mobile-overlay-open' : ''}`}
        onClick={closeMobileMenu}
        aria-hidden={!mobileMenuOpen}
      />
      <aside className={`view-client-mobile-drawer ${mobileMenuOpen ? 'view-client-mobile-drawer-open' : ''}`}>
        <div className="view-client-mobile-drawer-header">
          <span className="view-client-mobile-drawer-title">Menú</span>
          <button
            type="button"
            className="view-client-mobile-drawer-close"
            onClick={closeMobileMenu}
            aria-label="Cerrar menú"
          >
            <X size={24} />
          </button>
        </div>
        <nav className="view-client-mobile-drawer-nav">
          <button
            type="button"
            className={`view-client-mobile-nav-item ${section === 'citas' ? 'view-client-mobile-nav-item-active' : ''}`}
            onClick={handleNavCitas}
          >
            <Calendar size={20} />
            Mis citas
          </button>
          <button
            type="button"
            className={`view-client-mobile-nav-item ${section === 'datos' ? 'view-client-mobile-nav-item-active' : ''}`}
            onClick={handleNavDatos}
          >
            <User size={20} />
            Datos personales
          </button>
          <div className="view-client-mobile-drawer-user">
            Hola, {userName}
          </div>
          <button
            type="button"
            className="view-client-mobile-nav-item view-client-mobile-nav-item-logout"
            onClick={() => { closeMobileMenu(); handleLogout(); }}
          >
            <LogOut size={20} />
            Salir
          </button>
        </nav>
      </aside>

      <main className="view-client-main">
        {section === 'datos' ? (
          <DatosPersonales
            initialProfile={clientData ? {
              name: clientData.nombre,
              email: clientData.email,
              phone: clientData.telefono,
            } : null}
            onUserNameChange={setUserName}
            onProfileUpdate={setClientData}
          />
        ) : (
          <>
          <div className="view-client-title-row">
            <div>
              <h1 className="view-client-title">Mis citas</h1>
              <p className="view-client-subtitle">
                {totalAppointments > 0
                  ? `Mostrando ${appointments.length} de ${totalAppointments} citas`
                  : 'Aquí puedes ver todas tus citas agendadas'}
              </p>
            </div>
            <button
              type="button"
              className="view-client-btn-refresh"
              onClick={() => loadDashboard(0, false)}
              disabled={loading}
              aria-label="Recargar citas"
              title="Recargar citas"
            >
              <RefreshCw size={18} className={loading ? 'view-client-spin' : ''} />
            </button>
          </div>

          {loading ? (
            <div className="view-client-loading">
              <div className="view-client-spinner" />
              <p>Cargando citas...</p>
            </div>
          ) : appointments.length === 0 ? (
            <div className="view-client-empty">
              <Calendar size={48} />
              <p>No tienes citas agendadas</p>
              <button type="button" className="view-client-link-reservas" onClick={() => setShowAgendarModal(true)}>
                Reservar una cita
              </button>
            </div>
          ) : (
            <>
              <div className="view-client-list">
                {appointments.map((apt) => (
                  <div key={apt.id} className={`view-client-card status-${apt.status || 'pending'}`}>
                    <div className="view-client-card-header">
                      <span className="view-client-card-service">{apt.service_name || 'Servicio'}</span>
                      <span className="view-client-card-status">{statusLabel[apt.status] || apt.status}</span>
                    </div>
                    <div className="view-client-card-body">
                      <div className="view-client-card-row">
                        <User size={18} />
                        <span>{apt.barber_name || 'N/A'}</span>
                      </div>
                      <div className="view-client-card-row">
                        <Calendar size={18} />
                        <span>{formatDate(apt.appointment_date)}</span>
                      </div>
                      <div className="view-client-card-row">
                        <Clock size={18} />
                        <span>{formatTime(apt.appointment_time)} · {apt.duration_minutes || 0} min</span>
                      </div>
                      <div className="view-client-card-row view-client-card-price">
                        <span>{formatCurrency(apt.price)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {hasMore && (
                <div className="view-client-load-more-wrap">
                  <button
                    type="button"
                    className="view-client-btn-load-more"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? 'Cargando...' : 'Ver más citas'}
                  </button>
                </div>
              )}
            </>
          )}

          <div className="view-client-actions">
            <button type="button" className="view-client-btn-reservar" onClick={() => setShowAgendarModal(true)}>
              <Calendar size={20} />
              Reservar nueva cita
            </button>
          </div>
        </>
        )}

        {showAgendarModal && (
          <AgendarCitaModal
            isOpen={showAgendarModal}
            onClose={() => setShowAgendarModal(false)}
            clientData={clientData}
            onSuccess={handleReservationSuccess}
          />
        )}
      </main>
    </div>
  );
}
